import moment, { type Moment } from 'moment';
import { Formik } from 'formik';
import classNames from 'classnames';
import React, { useState } from 'react';
import { FiAlertCircle, FiCalendar } from 'react-icons/fi';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import DeleteButton from 'components/common/button/DeleteButton';
import DatePicker from 'components/common/datepicker/DatePicker';

import { useCreateNoticeMutation, useDeleteNoticeMutation, useUpdateNoticeMutation } from 'hooks/query/useUserInfo';
import noticeSchema from 'schemas/notice';

import history from 'utils/history';
import * as toast from 'utils/toast';

import * as routes from 'constants/routes';
import { DEFAULT_DATE_FORMAT } from 'constants/date';
import { DEFAULT_HISTORY_ENTRIES } from 'constants/appConstants';

type NoticeFormValues = {
  title: string;
  description: string | null;
  redirectUrl: string | null;
  startDate: Moment;
  endDate: Moment;
};

type NoticePayload = {
  title: string;
  description: string | null;
  redirectUrl: string | null;
  startDate: string;
  endDate: string;
};

type NoticeRecord = NoticeFormValues & { id: number };

const initialValues: NoticeFormValues = {
  title: '',
  description: null,
  redirectUrl: null,
  startDate: moment(),
  endDate: moment(),
};

interface Props {
  notice: NoticeRecord | null;
}

const NoticeForm: React.FC<Props> = ({ notice }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createNoticeMutation = useCreateNoticeMutation();
  const updateNoticeMutation = useUpdateNoticeMutation();
  const deleteNoticeMutation = useDeleteNoticeMutation();

  const handleClick = () => {
    return history.length > DEFAULT_HISTORY_ENTRIES ? history.goBack() : history.push(routes.NOTICES);
  };

  const handleSubmit = (values: NoticeFormValues) => {
    setIsSubmitting(true);

    const noticeObject: NoticePayload = {
      title: values.title.trim(),
      description: values.description ? values.description.trim() : null,
      redirectUrl: values.redirectUrl ? values.redirectUrl.trim() : null,
      startDate: values.startDate.clone().startOf('day').format(DEFAULT_DATE_FORMAT),
      endDate: values.endDate.clone().endOf('day').format(DEFAULT_DATE_FORMAT),
    };

    const onSuccess = () => {
      toast.success({
        title: 'Success',
        message: notice ? 'Notice updated successfully.' : 'Notice created successfully.',
      });
    };

    const onSettled = () => {
      setIsSubmitting(false);
      history.push(routes.NOTICES);
    };

    if (notice) {
      updateNoticeMutation.mutate({ id: notice.id, notice: noticeObject }, { onSuccess, onSettled });
    } else {
      createNoticeMutation.mutate(noticeObject, { onSuccess, onSettled });
    }
  };

  const handleDelete = () => {
    if (!notice) {
      return;
    }

    deleteNoticeMutation.mutate(notice.id, {
      onSuccess: () => {
        toast.success({
          title: 'Success',
          message: 'Notice deleted successfully.',
        });
      },
      onSettled: () => {
        history.push(routes.NOTICES);
      },
    });
  };

  return (
    <Formik<NoticeFormValues>
      initialValues={notice ? { ...notice } : { ...initialValues }}
      validationSchema={noticeSchema}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleSubmit, touched, errors, values, handleBlur, setFieldValue }) => {
        const buttons = [
          {
            text: 'Confirm',
            type: 'red',
            className: 'mr-12 delete__btn',
            onClick: handleDelete,
            close: true,
            showLoading: true,
          },
          {
            text: 'Cancel',
            type: 'outlined-grey',
            className: 'py-8 px-12',
            close: true,
          },
        ];

        return (
          <form onSubmit={handleSubmit} className="notice-form">
            <div className="full-scope-card__content">
              <div className="form-wrap">
                <div className="form-wrap__row form-wrap__row--no-margin clearfix">
                  <div className="form-wrap__col col-sm-6 px-0">
                    <div className="form-group width-full">
                      <label className="form-label dark--text">
                        Title <span className="required ml-5">*</span>
                      </label>
                      <input
                        name="title"
                        type="text"
                        value={values.title}
                        placeholder="Provide a suitable title for the notice"
                        className={classNames('form-elem form-elem--input-sm', {
                          'form-elem--error': Boolean(errors.title) && Boolean(touched.title),
                        })}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.title === 'string' && touched.title && (
                        <div className="error-block">{errors.title}</div>
                      )}
                    </div>
                    <div className="form-group width-full">
                      <label className="form-label dark--text">Description</label>
                      <textarea
                        name="description"
                        value={values.description || ''}
                        placeholder="Provide a suitable description for the notice"
                        className={classNames('form-elem form-elem--input-sm', {
                          'form-elem--error': Boolean(errors.description) && Boolean(touched.description),
                        })}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.description === 'string' && touched.description && (
                        <div className="error-block">{errors.description}</div>
                      )}
                    </div>
                    <div className="form-group width-full">
                      <label className="form-label dark--text">Link</label>
                      <input
                        name="redirectUrl"
                        type="text"
                        value={values.redirectUrl || ''}
                        placeholder="Provide link for the notice"
                        className={classNames('form-elem form-elem--input-sm', {
                          'form-elem--error': Boolean(errors.redirectUrl) && Boolean(touched.redirectUrl),
                        })}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      {typeof errors.redirectUrl === 'string' && touched.redirectUrl && (
                        <div className="error-block">{errors.redirectUrl}</div>
                      )}
                    </div>
                    <div className="d-flex flex-direction-responsive">
                      <div className="form-group width-full momr-20">
                        <label className="form-label dark--text">
                          Start Date <span className="required ml-5">*</span>
                        </label>
                        <div className="form-elem--icon-pos">
                          <DatePicker
                            id="startDate"
                            displayFormat="LL"
                            customInputIcon={<FiCalendar size={18} className="color-grey-50" />}
                            date={moment(values.startDate)}
                            onDateChange={(date) => {
                              const nextDate = moment(date);
                              if (!nextDate.isValid()) {
                                return;
                              }

                              setFieldValue('startDate', nextDate);
                            }}
                          />
                        </div>
                        {typeof errors.startDate === 'string' && touched.startDate && (
                          <div className="error-block">{errors.startDate}</div>
                        )}
                      </div>
                      <div className="form-group width-full">
                        <label className="form-label dark--text">
                          End Date<span className="required ml-5">*</span>
                        </label>

                        <div className="form-elem--icon-pos">
                          <DatePicker
                            id="endDate"
                            displayFormat="LL"
                            customInputIcon={<FiCalendar size={18} className="color-grey-50" />}
                            date={moment(values.endDate)}
                            onDateChange={(date) => {
                              const nextDate = moment(date);
                              if (!nextDate.isValid()) {
                                return;
                              }

                              setFieldValue('endDate', nextDate);
                            }}
                          />
                        </div>
                        {typeof errors.endDate === 'string' && touched.endDate && (
                          <div className="error-block">{errors.endDate}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="action-bar-footer action-bar-footer--bordered-top px-16">
                <button
                  type="submit"
                  className={classNames('btn btn--primary btn--curved mr-10', {
                    'btn--loading': isSubmitting,
                  })}
                  disabled={isSubmitting}
                >
                  <span className={isSubmitting ? 'invisible' : ''}>Save</span>
                  {isSubmitting && <Loading />}
                </button>
                <button
                  type="button"
                  className={classNames('btn btn--outlined-grey btn--curved mr-10')}
                  disabled={isSubmitting}
                  onClick={handleClick}
                >
                  Cancel
                </button>

                {notice && (
                  <LFModal
                    TriggerBtn={DeleteButton}
                    modalStatusIcon={<FiAlertCircle color="#F44336" size={24} />}
                    title={{ text: 'Delete this notice?', type: 'danger' }}
                    className="delete-modal"
                    message={{ text: "You won't be able to revert this!", type: 'delete__text' }}
                    renderFooter={true}
                    buttons={buttons}
                  />
                )}
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default NoticeForm;
